import React from 'react'
import { CircularProgress } from '@mui/material'
import { withAllContexts } from '../../../AllContextsWrapper'
import { enqueueSnackbar } from 'notistack';
import { Send, Clock, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import moment from 'moment';
import 'moment/locale/sk';

moment.locale('sk');

class Messages extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            new_message: '',
            sendButtonDisabled: false,
            sendRecapitulationButtonDisabled: false
        }
        this.messagesEndRef = React.createRef();
    }

    componentDidMount() {
        this.scrollToBottom();
    }

    componentDidUpdate() {
        this.scrollToBottom();
    }

    scrollToBottom = () => {
        this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    handleSendMessage = () => {
        if (this.state.new_message.trim() === '') {
            enqueueSnackbar("Správa nemôže byť prázdna", { variant: "error" });
            return;
        }

        this.setState({ sendButtonDisabled: true });

        let data = {
            to: this.props.reservation.client.phone,
            text: this.state.new_message,
            reservationId: this.props.reservation.id
        }

        this.props.socketIO.socket.emit('message:send', data, (response) => {
            if (response.status === 'success') {
                enqueueSnackbar("Správa úspešne odoslaná", { variant: "success" });
                this.setState({ new_message: '', sendButtonDisabled: false });
            } else {
                enqueueSnackbar("Správu sa nepodarilo odoslať", { variant: "error" });
                this.setState({ sendButtonDisabled: false });
            }
            this.handleReservationUpdate();
        });
    }

    handleReservationUpdate = () => {
        this.props.app.functions.updateMainCalendar();
        this.props.app.functions.updateCurrReservation(this.props.reservation.id);
    }

    handleSendRecapitulation = () => {
        this.setState({ sendRecapitulationButtonDisabled: true });

        this.props.socketIO.socket.emit('message:sendRecapitulation', this.props.reservation.id, (response) => {
            if (response.status === 'success') {
                enqueueSnackbar("Rekapitulácia úspešne odoslaná", { variant: "success" });
            } else {
                enqueueSnackbar("Rekapituláciu sa nepodarilo odoslať", { variant: "error" });
            }

            this.setState({ sendRecapitulationButtonDisabled: false });
            this.handleReservationUpdate();
        });
    }

    formatDate = (date) => {
        return moment(date).format('D.M.YYYY HH:mm');
    }

    render() {
        const { reservation } = this.props;
        // Sort by CreatedAt (oldest top) for chat flow
        const sortedMessages = (reservation.Messages || []).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        return (
            <div className="flex flex-col h-full bg-gray-50">
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {sortedMessages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <Send className="w-8 h-8 opacity-50" />
                            </div>
                            <p>Žiadne správy</p>
                        </div>
                    ) : (
                        sortedMessages.map((msg, index) => (
                            <div key={msg.id} className="flex justify-end">
                                <div className="max-w-[85%] min-w-[120px]">
                                    <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm relative">
                                        <div className="whitespace-pre-wrap text-sm">{msg.message}</div>

                                        {/* Status Indicators within Bubble */}
                                        <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-blue-100 opacity-80">
                                            <span>{this.formatDate(msg.createdAt)}</span>
                                            {msg.deliveryDateTime ? (
                                                <CheckCircle className="w-3 h-3" />
                                            ) : (
                                                <Clock className="w-3 h-3" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Detailed Status / Error underneath */}
                                    <div className="text-[10px] text-right mt-1 text-gray-400 px-1">
                                        {msg.deliveryDateTime ? (
                                            <span className="text-green-600 flex items-center justify-end gap-1">
                                                Doručená {this.formatDate(msg.deliveryDateTime)}
                                            </span>
                                        ) : (
                                            msg.error ? (
                                                <span className="text-red-500 flex items-center justify-end gap-1">
                                                    <AlertCircle className="w-3 h-3" /> Chyba doručenia
                                                </span>
                                            ) : (
                                                <span>Odosielanie...</span>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={this.messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="bg-white border-t border-gray-200 p-3 sm:p-4">
                    <div className="flex gap-2 items-end max-w-4xl mx-auto">
                        <div className="flex-1 bg-gray-100 rounded-2xl p-2 focus-within:ring-2 focus-within:ring-blue-100 focus-within:bg-white transition-all border border-transparent focus-within:border-blue-200">
                            <textarea
                                value={this.state.new_message}
                                onChange={(e) => this.setState({ new_message: e.target.value })}
                                placeholder="Napíšte správu..."
                                className="w-full bg-transparent border-0 focus:ring-0 p-1 text-sm resize-none max-h-32 min-h-[40px] outline-none"
                                rows={1}
                                onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        this.handleSendMessage();
                                    }
                                }}
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <button
                                onClick={this.handleSendMessage}
                                disabled={this.state.sendButtonDisabled || !this.state.new_message.trim()}
                                className={`p-3 rounded-full shadow-sm transition-all duration-200 flex items-center justify-center ${this.state.sendButtonDisabled || !this.state.new_message.trim()
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 active:scale-95'
                                    }`}
                            >
                                {this.state.sendButtonDisabled ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <Send className="w-5 h-5 ml-0.5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Recap Button */}
                    <div className="max-w-4xl mx-auto mt-2 flex justify-center">
                        <button
                            onClick={this.handleSendRecapitulation}
                            disabled={this.state.sendRecapitulationButtonDisabled}
                            className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${this.state.sendRecapitulationButtonDisabled
                                    ? 'bg-gray-50 text-gray-400 border-gray-200'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-blue-600 hover:border-blue-200'
                                }`}
                        >
                            {this.state.sendRecapitulationButtonDisabled ? (
                                <CircularProgress size={12} color="inherit" />
                            ) : (
                                <FileText className="w-3.5 h-3.5" />
                            )}
                            Poslať rekapituláciu objednávky
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default withAllContexts(Messages);
